/**
 * Debounced warning scheduler for an invalid date picker value.
 *
 * `debounce` from the `debounce` package cannot be used here: it is
 * trailing-edge with last-args and offers no way to drop a pending call, so an
 * invalid pick that the customer immediately corrects still announces itself
 * ~1s later, blaming a time that has become valid. This scheduler cancels on
 * `cancel()` and re-checks the date when the timer elapses, so a warning is
 * only shown for the value the picker is still holding.
 */
export const createInvalidDateNotifier = ({
  notify,
  wait = 1000,
  stillInvalid = () => true,
}) => {
  let timer = null;
  let pending = null;

  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    pending = null;
  };

  const schedule = (date, message) => {
    pending = { date, message };
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const { date: atDate, message: atMessage } = pending || {};
      pending = null;
      if (!atMessage) return;
      if (!stillInvalid(atDate)) return;
      notify(atMessage);
    }, wait);
  };

  return { schedule, cancel };
};
